// SPDX-License-Identifier: Apache-2.0

import { FormEvent, useContext, useEffect, useState } from "react";
import StellarAsset, { AssetID } from "../../api/types/stellarAsset";
import { AppContext } from "../../AppContext";
import { Button, Form, Row } from "react-bootstrap";
import { BsArrowDown, BsArrowRight } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import {
  assetLogos,
  ckbSwapReserve,
  getExplorerURL,
  Hubs,
  validSwapPairs,
} from "../../constants/Constants";
import {
  Chain,
  parseBalance,
  ReadBalance,
  Requirement,
  requirements,
  shortfalls,
  SwapSideKind,
} from "../../client/FundsCheck";
import { BiX } from "react-icons/bi";
import { assetPrecisions } from "../../constants/Constants";
import { parseUnits } from "ethers/lib/utils";
import { dispatchPerunError } from "../../events/Error";
import ErrorBoundary from "../ErrorBoundary";
import ReceiverSelection from "./ReceiverSelection";
import AssetDropdown from "./AssetDropDown";
import {
  calculateMinAmount,
  calculateTransactionFee,
  updateFromAmount,
  updateToAmount,
} from "../../client/GasCalculations";
// import { BigNumber } from "@ethersproject/bignumber";
import EthereumAsset from "../../api/types/ethereumAsset";
import { AddressLike } from "../../client/Provider";
import { utils } from "ethers";
import { dispatchPerunNotice, swapRateInfoNotice } from "../../events/Notice";
import { formatBaseUnits, truncateDecimal } from "../../web3/balanceUtils";
import Address from "../../api/types/address";
import { NervosAsset, SUDT } from "../../api/types/nervosAsset";
import {
  AssetGuardrail,
  indexGuardrailsByAsset,
} from "../../api/GetGuardrailsResponse";
import LimitsAndLiquidity from "./LimitsAndLiquidity";

interface Props {
  fromAsset?: AssetID;
  toAsset?: AssetID;
  ownEthAddress: Address | undefined;
  ownSteAddress: string | undefined;
  ownNervosAddress: string | undefined;
  submit: (
    fromAsset: EthereumAsset | StellarAsset | NervosAsset,
    toAsset: EthereumAsset | StellarAsset | NervosAsset,
    fromAmount: string,
    toAmount: string,
    fromDecimal: number,
    toDecimal: number,
    receiverAddress: string,
    rate: number,
  ) => void;
}

function AssetAmountForm(props: Props) {
  const { t } = useTranslation();
  const ctx = useContext(AppContext);

  const [validated, setValidated] = useState(false);
  const [chainID, setChainID] = useState<bigint[]>();
  const [assets, setAssets] = useState(
    new Map<AssetID, EthereumAsset | StellarAsset | NervosAsset>(),
  );
  const [fromAsset, setFromAsset] = useState<AssetID>();
  const [toAsset, setToAsset] = useState<AssetID>();
  const [fromAssetError, setFromAssetError] = useState<string>("");
  const [toAssetError, setToAssetError] = useState<string>("");
  const [balString, setBalString] = useState<string>("");

  const [swapDecimals, setSwapDecimals] = useState<[number, number]>([0, 0]);
  const [exchangeRate, setExchangeRate] = useState(0);
  const [fromAssetPrecision, setFromAssetPrecision] = useState(0);
  const [toAssetPrecision, setToAssetPrecision] = useState(0);
  const [fromAmount, setFromAmount] = useState<string>();
  const [toAmount, setToAmount] = useState<string>();
  const [fromInput, setFromInput] = useState<string>();
  const [toInput, setToInput] = useState<string>();
  const [ownBalSuff] = useState<boolean>(true);

  const [ownChecked, setOwnChecked] = useState(true);
  const [receiverAddress, setReceiverAddress] = useState<string>();
  const [otherReceiverAddress, setOtherReceiverAddress] = useState<string>();
  const [fromTxFee, setFromTxFee] = useState<string>("0.0000");
  const [toTxFee, setToTxFee] = useState<string>("0.0000");
  const [minSwapAmount, setMinSwapAmount] = useState<string>();
  const [validSwapAmount, setValidSwapAmount] = useState<boolean>(false);
  const [guardrails, setGuardrails] = useState<Map<string, AssetGuardrail>>(new Map());
  const [guardrailsLoaded, setGuardrailsLoaded] = useState<boolean>(false);
  const [fromGuardrailError, setFromGuardrailError] = useState<string>("");
  const [toGuardrailError, setToGuardrailError] = useState<string>("");
  // Gas price the quote gave for the paying chain, for the user's own deposit.
  const [fromGasPrice, setFromGasPrice] = useState<bigint>(0n);
  // CKB in the user's Nervos wallet while a pair with CKB is selected; null
  // when no such pair is selected, undefined when it could not be read.
  const [ckbBalance, setCkbBalance] = useState<ReadBalance | null>(null);
  /**
   * setDecimals sets the swapDecimals for the assets of the swap-pair.
   */
  const setDecimals = (
    swapPair: (EthereumAsset | StellarAsset | NervosAsset)[],
  ) => {
    if (!swapPair[0] || !swapPair[1]) {
      return;
    }
    Promise.all([
      ctx.client.getDecimals(swapPair[0].toChannelAsset()),
      ctx.client.getDecimals(swapPair[1].toChannelAsset()),
    ]).then((decAssets) => {
      console.log("decAssets:", decAssets);
      if (decAssets[0] !== undefined && decAssets[1] !== undefined) {
        console.log("decAssets:", decAssets);
        setSwapDecimals([decAssets[0], decAssets[1]]);
      }
    });
  };

  useEffect(() => {
    console.log("receiverAddress:", receiverAddress?.toString());
  }, [receiverAddress]);

  useEffect(() => {
    if (!assets.size) {
      const assetMap = new Map<
        string,
        EthereumAsset | StellarAsset | NervosAsset
      >();
      ctx.client
        .getAssets({ type: "all" })
        .then((aRsp) => {
          console.log("Assets: ", aRsp, "address:", props.ownNervosAddress);
          if (
            props.ownEthAddress &&
            aRsp.ethereumAssets
          ) {
            aRsp.ethereumAssets.map((a) => assetMap.set(a.assetID(), a));
          }
          if (props.ownSteAddress && aRsp.stellarAssets) {
            aRsp.stellarAssets.map((a) => assetMap.set(a.assetID(), a));
          }
          if (props.ownNervosAddress && aRsp.nervosAssets) {
            aRsp.nervosAssets.map((a) => {
              console.log("asset: ", a.assetID());
              assetMap.set(a.assetID(), a);
            });
          }
          console.log("Setting assets: ", assetMap);
          setAssets(assetMap);
        })
        .catch((error) => {
          console.error("Error fetching assets:", error);
        });
      ctx.client
        .getChainID()
        .then((chainID) => setChainID(chainID))
        .catch((error) => {
          console.error("Error fetching chain id:", error);
        });
    }
  }, [assets]);

  useEffect(() => {
    setGuardrails(new Map());
    setGuardrailsLoaded(false);
    setFromGuardrailError("");
    setToGuardrailError("");
    if (assets.size > 0 && fromAsset && toAsset) {
      const assetList = Array.from(assets.values());
      let hub = Hubs.get(assets.get(toAsset!)!.chainID)!;
      if (assets.get(fromAsset!)!.chainID === 2n && assets.get(toAsset!)!.chainID === 11155111n) {
        hub = Hubs.get(assets.get(fromAsset!)!.chainID + assets.get(toAsset!)!.chainID)!;
      }
      ctx.client
        .getGuardrails(assetList, hub.address)
        .then((resp) => {
          if (resp) {
            setGuardrails(indexGuardrailsByAsset(resp.guardrails, assetList));
          }
          setGuardrailsLoaded(true);
        })
        .catch((err) => {
          console.error("Error fetching guardrails:", err);
          setGuardrailsLoaded(true);
        });
    }
  }, [fromAsset, toAsset]);

  useEffect(() => {
    setExchangeRate(0);
    if (fromAsset) {
      let address: string;
      if (assets.get(fromAsset) instanceof EthereumAsset) {
        address = ctx.client.ethProvider!.getAddress();
      } else if (assets.get(fromAsset) instanceof StellarAsset) {
        address = ctx.client.steProvider!.getAddress();
      } else {
        address = ctx.client.ckbProvider!.getAddress();
      }
      getBal(address, assets.get(fromAsset!)!).then(async (bal) => {
        if (bal === "") {
          dispatchPerunError(<p>{t("errorFetchingBalance")}</p>);
          console.log("Error fetching balance");
        } else {
          if (assets.get(fromAsset!)! instanceof EthereumAsset) {
            const dec = await ctx.client.getDecimals(
              assets.get(fromAsset!)!.toChannelAsset(),
            );
            const etherBalance = utils.formatUnits(bal, dec!);
            setBalString(parseFloat(etherBalance).toFixed(4).toString());
            setBalString(etherBalance);
          } else if (assets.get(fromAsset!)! instanceof NervosAsset) {
            const dec = await ctx.client.getDecimals(
              assets.get(fromAsset!)!.toChannelAsset(),
            );
            const ckbBalance = utils.formatUnits(bal, dec!);
            setBalString(parseFloat(ckbBalance).toFixed(4).toString());
            setBalString(ckbBalance);
          } else {
            setBalString(bal);
          }
        }
      });
    }
    console.log("fromAsset:", fromAsset);
    console.log("toAsset:", toAsset);
    setDecimals([assets.get(fromAsset!)!, assets.get(toAsset!)!]);
  }, [fromAsset, toAsset]);

  useEffect(() => {
    if (fromAsset && toAsset) {
      console.log("Assets set");
      setFromAmount(undefined);
      setToAmount(undefined);
      setFromInput(undefined);
      setToInput(undefined);
      let hub = Hubs.get(assets.get(toAsset!)!.chainID)!
      if (assets.get(fromAsset!)!.chainID === 2n && assets.get(toAsset!)!.chainID === 11155111n) {
        hub = Hubs.get(assets.get(fromAsset!)!.chainID + assets.get(toAsset!)!.chainID)!
      }
      console.log("HUB: ", hub);
      ctx.client
        .getQuote(
          assets.get(fromAsset!)!.toChannelAsset(),
          assets.get(toAsset!)!.toChannelAsset(),
          hub.address,
        )
        .then((response) => {
          if (response === undefined || response.crossQuote === 0) {
            console.log("undefined", response);
            dispatchPerunError(<p>{t("errorGettingExchangeRate")}</p>);
            return;
          }
          console.log("response:", response);
          const txVars = calculateTransactionFee(
            assets,
            fromAsset,
            toAsset,
            response.crossQuote,
            response.fromQuote,
            response.toQuote,
            response.fromGas,
            response.toGas,
          );
          console.log("txVars:", txVars);
          setFromGasPrice(parseBalance(response.fromGas) ?? 0n);
          setFromTxFee(txVars.fromTxFee.toString());
          setToTxFee(txVars.toTxFee.toString());
          setExchangeRate(txVars.exchangeRate);
        });
      setFromAssetPrecision(
        assetPrecisions.get(assets.get(fromAsset!)!.code) || 0,
      );
      console.log("TO ASSET: ", assets.get(toAsset!)!.code, assetPrecisions.get(assets.get(toAsset!)!.code));
      setToAssetPrecision(assetPrecisions.get(assets.get(toAsset!)!.code) || 0);
    }
  }, [swapDecimals]);

  // swapKind maps an asset to the kinds the funds check knows. Other assets
  // (Stellar, tokens on CKB) keep the previous check.
  const swapKind = (
    a: EthereumAsset | StellarAsset | NervosAsset | undefined,
  ): SwapSideKind | undefined => {
    if (a instanceof EthereumAsset) {
      return a.type === "ETH" ? "ETH" : a.type === "ERC20" ? "ERC20" : undefined;
    }
    if (a instanceof NervosAsset && a.type === "CKB") return "CKB";
    return undefined;
  };

  const chainAddress = (chain: Chain): string =>
    (chain === "Nervos"
      ? ctx.client.ckbProvider?.getAddress()
      : ctx.client.ethProvider?.getAddress()) ?? "";

  const shortfallMessage = (r: Requirement): string => {
    const address = chainAddress(r.chain);
    if (r.have === undefined) {
      return t("balanceUnavailable", { asset: r.asset, chain: r.chain, address });
    }
    const msg = t("fundsShortfall", {
      asset: r.asset,
      chain: r.chain,
      address,
      need: formatBaseUnits(r.need, r.decimals, 6, "up"),
      have: formatBaseUnits(r.have, r.decimals),
    });
    return r.chain === "Nervos"
      ? `${msg} ${t("fundsShortfallDeposit", { deposit: formatBaseUnits(ckbSwapReserve, 8) })}`
      : msg;
  };

  // With CKB on either side the user opens the channel on Nervos, which needs a
  // deposit in the Nervos wallet derived from the connected account. Read that
  // balance as soon as the pair is chosen so the requirement is visible before
  // the amount is typed, not only as an error after "Swap".
  useEffect(() => {
    setCkbBalance(null);
    const pair = [fromAsset, toAsset].map((id) => (id ? assets.get(id) : undefined));
    const ckbAsset = pair.find((a) => swapKind(a) === "CKB");
    if (!pair[0] || !pair[1] || !ckbAsset || !ctx.client.ckbProvider) return;
    getBal(ctx.client.ckbProvider.getAddress(), ckbAsset)
      .then((bal) => setCkbBalance(parseBalance(bal)))
      .catch(() => setCkbBalance(undefined));
  }, [fromAsset, toAsset, assets]);

  useEffect(() => {
    if (exchangeRate !== 0) {
      setMinSwapAmount(
        calculateMinAmount(exchangeRate, toTxFee!, fromAssetPrecision),
      );
    }
  }, [exchangeRate, toTxFee, fromAssetPrecision]);

  useEffect(() => {
    if (fromAmount) {
      if (
        parseFloat(fromAmount!) >= parseFloat(minSwapAmount! as string) &&
        parseFloat(fromAmount!) > 0
      ) {
        setValidSwapAmount(true);
      }
    }
    if (toAmount) {
      if (parseFloat(toAmount!) > 0) {
        setValidSwapAmount(true);
      }
    }
  }, [fromAmount, toAmount]);

  const onSubmitHandler = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    console.log("form validity:", form.checkValidity());
    console.log("validation:", validate());
    if (fromGuardrailError || toGuardrailError) {
      return;
    }
    if (form.checkValidity() && validate()) {
      let fromAmountNum, toAmountNum;
      try {
        fromAmountNum = parseUnits(fromAmount!, swapDecimals[0]);
        toAmountNum = parseUnits(toAmount!, swapDecimals[1]);
      } catch (e) {
        console.log("could not parse units", e);
        return;
      }

      // Safety check that the amounts are greater than 0.
      if (fromAmountNum.lte(0) || toAmountNum.lte(0)) {
        console.log("Amounts not greater than 0", fromAmountNum, toAmountNum);
        return;
      }

      const { fromBalance, toBalance, fromNativeBalance, toNativeBalance } = await getBalances();
      const fromKind = swapKind(assets.get(fromAsset!));
      const toKind = swapKind(assets.get(toAsset!));
      if (fromKind && toKind) {
        const missing = shortfalls(
          requirements({
            from: {
              kind: fromKind,
              code: getTokenCode(assets.get(fromAsset!)!),
              decimals: swapDecimals[0],
            },
            to: { kind: toKind },
            fromAmount: fromAmountNum.toBigInt(),
            gasPrice: fromGasPrice,
            ckbReserve: ckbSwapReserve,
            balances: {
              from: parseBalance(fromBalance),
              eth: fromKind === "ERC20" ? parseBalance(fromNativeBalance) : undefined,
              ckb: toKind === "CKB" ? parseBalance(toBalance) : undefined,
            },
          }),
        );
        if (missing.length > 0) {
          console.log("insufficient funds", missing);
          dispatchPerunError(
            <>
              {missing.map((r) => (
                <p key={`${r.chain}-${r.asset}`} className={"funds-shortfall"}>
                  {shortfallMessage(r)}
                </p>
              ))}
            </>,
          );
          return;
        }
      } else if (fromBalance === "" || fromBalance === "0" || fromNativeBalance === "" || fromNativeBalance === "0" || toNativeBalance === "" || toNativeBalance === "0") {
        console.log("Error fetching balances");
        dispatchPerunError(<p>{t("fundAddress")}</p>);
        return;
      }
      setValidated(true);
      if (validate()) {
        console.log("validated");
        // Resolve the receiver into a local value and submit with that. The
        // state setter does not update `receiverAddress` within this handler,
        // so reading the state here submitted nothing on the first press and
        // only worked on the second, with the value the first press stored.
        let receiver: string | undefined;
        if (!ownChecked) {
          console.log("other address:", otherReceiverAddress!, ownChecked);
          try {
            if (assets!.get(toAsset!) instanceof EthereumAsset) {
              let otherAddress = otherReceiverAddress!;
              otherAddress = otherAddress.startsWith("0x")
                ? otherAddress
                : "0x" + otherAddress;
              receiver = otherAddress;
            } else {
              receiver = otherReceiverAddress!;
            }
          } catch (e) {
            console.error("invalid address", e);
            return;
          }
        } else {
          if (assets!.get(toAsset!) instanceof EthereumAsset) {
            console.log("own address:", props.ownEthAddress!);
            receiver = props.ownEthAddress!.toString();
          } else if (assets!.get(toAsset!) instanceof StellarAsset) {
            console.log("own address:", props.ownSteAddress!);
            receiver = props.ownSteAddress!;
          } else {
            console.log("own address:", props.ownNervosAddress!);
            receiver = props.ownNervosAddress!;
          }
        }
        setReceiverAddress(receiver);
        if (receiver) {
          console.log("submitting");
          props.submit(
            assets.get(fromAsset!)!,
            assets.get(toAsset!)!,
            fromAmountNum.toString(),
            toAmountNum.toString(),
            swapDecimals[0],
            swapDecimals[1],
            receiver,
            exchangeRate,
          );
          setValidated(false);
        }
      }
    } else {
      console.log(form.reportValidity());
    }
  };

  const parseStellarBalance = (balance: string): string => {
    const num = parseInt(balance, 10);
    return (num / 10000000).toFixed(7);
  };

 const getBalances = async () => {
   let fromBalance = "";
   let toBalance = "";
   let fromNativeBalance = "";
   let toNativeBalance = "";

   if (fromAsset) {
     let address: string;
     if (assets.get(fromAsset) instanceof EthereumAsset) {
       console.log("Getting balance for Ethereum asset");
       address = ctx.client.ethProvider!.getAddress();
     } else if (assets.get(fromAsset) instanceof StellarAsset) {
        console.log("Getting balance for Stellar asset");
       address = ctx.client.steProvider!.getAddress();
     } else {
        console.log("Getting balance for Nervos asset");
       address = ctx.client.ckbProvider!.getAddress();
     }
     fromBalance = await getBal(address, assets.get(fromAsset!)!);
     fromNativeBalance = fromBalance;
     console.log("fromBalance:", fromBalance);
   }
   if (toAsset) {
      let address: string;
      if (assets.get(toAsset) instanceof EthereumAsset) {
        address = ctx.client.ethProvider!.getAddress();
      } else if (assets.get(toAsset) instanceof StellarAsset) {
        address = ctx.client.steProvider!.getAddress();
      } else {
        address = ctx.client.ckbProvider!.getAddress();
      }
      if (assets.get(toAsset) instanceof EthereumAsset && assets.get(fromAsset!) instanceof EthereumAsset) {
        console.log("fromNativeBalance used for toBalance");
        toBalance = fromNativeBalance;
        toNativeBalance = fromNativeBalance;
        return { fromBalance, toBalance, fromNativeBalance, toNativeBalance };
      }
      toBalance = await getBal(address, assets.get(toAsset!)!);
      toNativeBalance = toBalance;
      console.log("toBalance:", toBalance);
   }
   if (assets.get(fromAsset!)?.type === "ERC20") {
      let address = ctx.client.ethProvider!.getAddress();
      fromNativeBalance = await ctx.client.ethProvider!.getETHBalance(
        undefined,
        address,
      );
      console.log("fromNativeBalance:", fromNativeBalance);
   }
   if (assets.get(fromAsset!)?.type === "credit") {
      let address = ctx.client.steProvider!.getAddress();
      fromNativeBalance = await getBal(address, new StellarAsset("XLM", "XLM", "native", "d7928b72c2703ccfeaf7eb9ff4ef4d504a55a8b979fc9b450ea2c842b4d1ce61", 2n));
      console.log("fromNativeBalance:", fromNativeBalance);
   }
   if (assets.get(fromAsset!)?.type === "SUDT") {
      let address = ctx.client.ckbProvider!.getAddress();
      fromNativeBalance = await getBal(address, new NervosAsset("CKB", "CKB", 8, 3n, new SUDT(0, "0x0000000000000000000000000000000000000000000000000000000000000000", "", "0x"), "CKB"))
      console.log("fromNativeBalance:", fromNativeBalance);
   }
   if (assets.get(toAsset!)?.type === "ERC20") {
     if (assets.get(fromAsset!) instanceof EthereumAsset) {
       console.log("fromNativeBalance used for toNativeBalance");
       toNativeBalance = fromNativeBalance;
     } else {
       let address = ctx.client.ethProvider!.getAddress();
       toNativeBalance = await ctx.client.ethProvider!.getETHBalance(
         undefined,
         address,
       );
       console.log("toNativeBalance:", toNativeBalance);
     }
   }
   if (assets.get(toAsset!)?.type === "credit") {
     let address = ctx.client.steProvider!.getAddress();
     toNativeBalance = await getBal(address, new StellarAsset("XLM", "XLM", "native", "d7928b72c2703ccfeaf7eb9ff4ef4d504a55a8b979fc9b450ea2c842b4d1ce61", 2n));
     console.log("toNativeBalance:", toNativeBalance);
   }
   if (assets.get(toAsset!)?.type === "SUDT") {
     let address = ctx.client.ckbProvider!.getAddress();
     toNativeBalance = await getBal(address, new NervosAsset("CKB", "CKB", 8, 3n, new SUDT(0, "0x0000000000000000000000000000000000000000000000000000000000000000", "", "0x"), "CKB"))
     console.log("toNativeBalance:", toNativeBalance);
   }
   return { fromBalance, toBalance, fromNativeBalance, toNativeBalance };
 }

  const getBal = async (
    address: AddressLike,
    asset: EthereumAsset | StellarAsset | NervosAsset,
  ): Promise<string> => {
    if (asset instanceof StellarAsset) {
      return ctx.client
        .getBalance(asset.toChannelStellarAsset())
        .then((response) => {
          if (response === undefined) {
            return "";
          }
          console.log("balance:", response.balance);
          return response.balance;
        });
    } else if (asset instanceof NervosAsset) {
      return ctx.client
        .getBalance(asset.toNervosChannelAsset())
        .then((response) => {
          if (response === undefined) {
            return "";
          }
          console.log("balance:", response.balance);
          return response.balance;
        });
    } else {
      {
        {
          switch (asset.type) {
            case "ETH":
              return ctx.client.ethProvider!.getETHBalance(undefined, address);
            case "ERC20":
              return ctx.client.ethProvider!.getERC20Balance(
                asset.address,
                address,
              );
          }
          return "";
        }
      }
    }
    return "";
  };

  const validate = (): boolean => {
    let valid = true;
    setFromAssetError("");
    setToAssetError("");

    if (!fromAsset) {
      setFromAssetError(t("fromTokenNotSet"));
      valid = false;
    }
    if (!toAsset) {
      setToAssetError(t("toTokenNotSet"));
      valid = false;
    }
    if (fromAsset === toAsset) {
      setToAssetError(t("invalidSwapPair"));
      valid = false;
    }
    return valid;
  };

  const checkGuardrail = (assetID: AssetID | undefined, amount: string | undefined, decimals: number): string => {
    if (!assetID || !amount || !guardrails.has(assetID)) return "";
    const guardrail = guardrails.get(assetID)!;
    const thresholdFormatted = utils.formatUnits(guardrail.threshold, decimals);
    if (parseFloat(amount) > parseFloat(thresholdFormatted)) {
      return t("amountExceedsGuardrail", {
        threshold: thresholdFormatted,
        asset: getTokenCode(assets.get(assetID)!),
      });
    }
    return "";
  };

  const handleUpdateFromAmount = (amount: string) => {
    setValidSwapAmount(false);
    setFromInput(amount);
    const amounts = updateFromAmount(
      amount,
      exchangeRate,
      fromTxFee!,
      toTxFee!,
      fromAssetPrecision,
      toAssetPrecision,
    );
    setToAmount(amounts.toAmount);
    setToInput(amounts.toInput);
    setFromAmount(amounts.fromAmount);
    setFromGuardrailError(checkGuardrail(fromAsset, amounts.fromAmount, swapDecimals[0]));
    setToGuardrailError(checkGuardrail(toAsset, amounts.toAmount, swapDecimals[1]));
  };

  const handleUpdateToAmount = (amount: string) => {
    setValidSwapAmount(false);
    setToInput(amount);
    const amounts = updateToAmount(
      amount,
      exchangeRate,
      fromTxFee!,
      toTxFee!,
      fromAssetPrecision,
      toAssetPrecision,
    );
    setFromAmount(amounts.fromAmount);
    setToAmount(amounts.toAmount);
    setFromInput(amounts.fromInput);
    setFromGuardrailError(checkGuardrail(fromAsset, amounts.fromAmount, swapDecimals[0]));
    setToGuardrailError(checkGuardrail(toAsset, amounts.toAmount, swapDecimals[1]));
  };

  const formatNumber = (num: number) => {
    const threshold = 1e-3;

    if (Math.abs(num) < threshold) {
      return num.toExponential(5);
    } else {
      return num.toFixed(5);
    }
  };

  return (
    <>
      <ErrorBoundary>
        <LimitsAndLiquidity assets={assets} />
        <Row className={"flex-fill justify-content-center g-0"}>
          <Form
            className={"swap-form mx-auto text-center"}
            noValidate
            validated={validated}
            onSubmit={onSubmitHandler}
          >
            <Form.Group className="mb-3 w-100" controlId={"fromAssetForm"}>
              <Form.Label style={{ fontSize: "110%" }}>
                {t("selectPairandAmount")}
              </Form.Label>
              {fromAsset && toAsset && exchangeRate === 0 && (
                // The amount field stays disabled until the hub's quote comes
                // back, which routes through an oracle price lookup that can
                // take several seconds on an uncached asset. Without this the
                // field just sits inert with no indication anything is
                // happening.
                <p className={"fetching-rate-hint"}>{t("fetchingExchangeRate")}</p>
              )}
              <div className={"inner-row"}>
                <div className="d-flex">
                  <AssetDropdown
                    assets={assets}
                    selectedAsset={fromAsset}
                    otherAsset={toAsset}
                    chainID={chainID}
                    onSelectAsset={(assetID) => {
                      if (assetID && toAsset !== assetID) {
                        setFromAsset(assetID);
                        if (toAsset) {
                          setDecimals([
                            assets.get(fromAsset!)!,
                            assets.get(toAsset!)!,
                          ]);
                        }
                      }
                    }}
                  />
                  <div className="d-flex flex-column">
                    <Form.Control
                      disabled={!fromAsset || !toAsset || exchangeRate === 0}
                      type="text"
                      value={fromInput || ""}
                      placeholder={"0.0"}
                      lang="en"
                      min={minSwapAmount ? parseFloat(minSwapAmount! as string): 0}
                      step={1 / 10 ** fromAssetPrecision}
                      required
                      isInvalid={!ownBalSuff}
                      onChange={(event) =>
                        handleUpdateFromAmount(event.currentTarget.value)
                      }
                    />
                  </div>
                  <Form.Control.Feedback type="invalid">
                    {ownBalSuff
                      ? t("invalidSwapAmount")
                      : t("insufficientBalance")}
                  </Form.Control.Feedback>
                </div>
                <div className={"tx-fee"}>
                  <p>+ {truncateDecimal(fromTxFee! as string, 4, "UP")}</p>
                </div>
              </div>
              {fromGuardrailError && (
                <span className="invalid-feedback" style={{ display: "block" }}>
                  {fromGuardrailError}
                </span>
              )}
              {fromAssetError.length > 0 && (
                <span className="invalid-feedback" style={{ display: "block" }}>
                  {fromAssetError}
                </span>
              )}
            </Form.Group>
            {balString &&
              fromAsset &&
              (assets!.get(fromAsset!) instanceof EthereumAsset ||
              assets!.get(fromAsset!) instanceof NervosAsset ? (
                <p>{`Balance: ${balString} ${assets!.get(fromAsset!)?.name}`}</p>
              ) : (
                <p>{`Balance: ${parseStellarBalance(balString)} ${assets!.get(fromAsset!)?.name}`}</p>
              ))}
            <BsArrowDown id={"swap-arrow-down"} />
            <Row className={"rate"}>
              {toAsset && fromAsset ? (
                <p>
                  {t("rate")}: 1 {getTokenCode(assets.get(fromAsset!)!)} ≈{" "}
                  {formatNumber(exchangeRate)}{" "}
                  {getTokenCode(assets.get(toAsset!)!)}
                </p>
              ) : null}
            </Row>
            {ckbBalance !== null && fromAsset && toAsset && (
              <p className={"ckb-reserve-hint"}>
                {t(
                  swapKind(assets.get(fromAsset)) === "CKB"
                    ? "ckbReserveNoticePay"
                    : "ckbReserveNoticeReceive",
                  {
                    address: chainAddress("Nervos"),
                    deposit: formatBaseUnits(ckbSwapReserve, 8),
                    have:
                      ckbBalance === undefined
                        ? t("unknown")
                        : formatBaseUnits(ckbBalance, 8),
                  },
                )}
              </p>
            )}

            <Form.Group className="mb-3 w-100" controlId={"toAssetForm"}>
              <div className={"inner-row"}>
                <div className="d-flex">
                  <AssetDropdown
                    assets={assets}
                    selectedAsset={toAsset}
                    otherAsset={fromAsset}
                    onSelectAsset={(assetID) => {
                      if (assetID && fromAsset !== assetID) {
                        setToAsset(assetID);
                        console.log("toAsset:", assets!.get(toAsset!));
                        if (toAsset) {
                          setDecimals([
                            assets.get(fromAsset!)!,
                            assets.get(toAsset!)!,
                          ]);
                        }
                      }
                    }}
                  />
                  <Form.Control
                    disabled={!fromAsset || !toAsset || exchangeRate === 0}
                    type="text"
                    value={toInput || ""}
                    placeholder={"0.0"}
                    lang="en"
                    min={1 / 10 ** toAssetPrecision}
                    step={1 / 10 ** toAssetPrecision}
                    required
                    onChange={(event) =>
                      handleUpdateToAmount(event.currentTarget.value)
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {t("invalidSwapAmount")}
                  </Form.Control.Feedback>
                </div>
                <div className={"tx-fee"}>
                  <p>- {truncateDecimal(toTxFee! as string, 4, "UP")}</p>
                </div>
              </div>
              {toGuardrailError && (
                <span className="invalid-feedback" style={{ display: "block" }}>
                  {toGuardrailError}
                </span>
              )}
              {toAssetError.length > 0 && (
                <span className="invalid-feedback" style={{ display: "block" }}>
                  {toAssetError}
                </span>
              )}
            </Form.Group>
            {fromAmount && toAmount && validSwapAmount && (
              <Row className={"inner-row"}>
                <div className={"amount-selection"}>
                  <p>
                    {truncateDecimal(fromAmount, fromAssetPrecision, "UP")}{" "}
                    {getTokenCode(assets.get(fromAsset!)!)}
                  </p>
                  <BsArrowRight className="arrow-icon" />
                  <p>
                    {truncateDecimal(toAmount, toAssetPrecision, "DOWN")}{" "}
                    {getTokenCode(assets.get(toAsset!)!)}
                  </p>
                </div>
                <div
                  className="help-button"
                  onClick={() =>
                    dispatchPerunNotice(
                      swapRateInfoNotice(
                        fromTxFee!,
                        toTxFee!,
                        exchangeRate,
                        assets.get(fromAsset!)!.chainID,
                        assets.get(toAsset!)!.chainID,
                        getTokenCode(assets.get(fromAsset!)!),
                        getTokenCode(assets.get(toAsset!)!),
                      ),
                    )
                  }
                >
                  <span>?</span>
                </div>
              </Row>
            )}
            {!validSwapAmount && (
              <Row className={"inner-row"}>
                <p>
                  {t("minAmount")}
                  {minSwapAmount}
                </p>
              </Row>
            )}
            <Button
              className={"clear-button"}
              variant={"light"}
              onClick={() => {
                setFromAsset(undefined);
                setToAsset(undefined);
                setFromAmount(undefined);
                setToAmount(undefined);
                setFromInput(undefined);
                setToInput(undefined);
                setFromAssetError("");
                setToAssetError("");
                setBalString("");
                setValidated(false);
                setFromTxFee("0.0000");
                setToTxFee("0.0000");
                setGuardrailsLoaded(false);
                setFromGuardrailError("");
                setToGuardrailError("");
              }}
            >
              <BiX size={20} />
              {t("clear")}
            </Button>
            <br />
            {toAsset && (
              <ReceiverSelection
                ownAddress={
                  assets!.get(toAsset) instanceof EthereumAsset
                    ? props.ownEthAddress!
                    : assets!.get(toAsset) instanceof StellarAsset
                      ? props.ownSteAddress!
                      : props.ownNervosAddress!
                }
                submit={(address: string, oC: boolean) => {
                  // 1. case: ToAsset is EthereumAsset
                  if (assets!.get(toAsset) instanceof EthereumAsset) {
                    if (
                      props.ownEthAddress &&
                      address !== props.ownEthAddress!.toString() &&
                      !oC
                    ) {
                      setOtherReceiverAddress(address as string);
                      setOwnChecked(false);
                    } else {
                      setOwnChecked(true);
                    }
                    // 2. case: ToAsset is StellarAsset
                  } else if (assets!.get(toAsset) instanceof StellarAsset) {
                    if (
                      props.ownSteAddress &&
                      address !== props.ownSteAddress! &&
                      !oC
                    ) {
                      setOtherReceiverAddress(address as string);
                      setOwnChecked(false);
                    } else {
                      setOwnChecked(true);
                    } // 3. case: ToAsset is NervosAsset
                  } else {
                    if (
                      props.ownNervosAddress &&
                      address !== props.ownNervosAddress! &&
                      !oC
                    ) {
                      setOtherReceiverAddress(address as string);
                      setOwnChecked(false);
                    } else {
                      setOwnChecked(true);
                    }
                  }
                }}
              ></ReceiverSelection>
            )}
            <Button
              className={"asset-form-submit custom-button padding"}
              variant={"primary"}
              type={"submit"}
              disabled={!guardrailsLoaded || !!fromGuardrailError || !!toGuardrailError}
            >
              {t("next")}
            </Button>
          </Form>
        </Row>
      </ErrorBoundary>
    </>
  );
}

/**
 * Returns a component displaying the asset with its logo.
 *
 * @param asset
 * @param withName - Whether the asset should be displayed with its full name or
 * only by its code, e.g., "Ethereum (ETH)" or "ETH".
 * @param withBorder - Whether the component should be inside a container with
 * a border.
 */
export const getTokenTitle = (
  asset: EthereumAsset | StellarAsset | NervosAsset,
  withName: boolean,
  withBorder: boolean,
): JSX.Element => {
  const logo = assetLogos.get(asset.code);
  const img = logo ? (
    <img className={"asset-logo"} src={logo} alt={`${asset.code}-logo`} />
  ) : (
    <></>
  );
  const name = withName
    ? `${asset.name} (${getTokenCode(asset)})`
    : getTokenCode(asset);

  if (withBorder) {
    return (
      <div className={"asset-container"}>
        {img} {name}
      </div>
    );
  } else {
    return (
      <>
        {img} {name}
      </>
    );
  }
};

export const getTokenCode = (
  asset: EthereumAsset | StellarAsset | NervosAsset,
  link?: boolean,
): string => {
  if (link && asset instanceof StellarAsset) {
    return getExplorerURL(asset.chainID, asset.contractID, "token");
  } else {
    if (asset.code === "GOEETH" /* && asset.chainID === 5n*/) {
      return "GOE-ETH";
    } else if (asset.code === "GOE") {
      return "GOE-ERC20";
    } else if (asset.code === "BLC") {
      return "BLC-ERC20";
    } else if (asset.code === "SEPETH") {
      return "SEP-ETH";
    } else if (asset.code === "RLC") {
      return "ETH-RLC";
    } else {
      return asset.code;
    }
  }
};

export const validPair = (
  fromAsset: EthereumAsset | StellarAsset | NervosAsset,
  toAsset: EthereumAsset | StellarAsset | NervosAsset,
): boolean => {
  return validSwapPairs.some(
    (pair) => pair.includes(fromAsset.code) && pair.includes(toAsset.code),
  );
};

export default AssetAmountForm;
